import { EventEmitter, inject, Injectable } from '@angular/core';
import { SharedAppSettings, SharedAppSettingsService } from '@luggagehero/shared/app-settings/data-access';
import { CoordinateSource, ILocation, ILuggage, ITimePeriod, StorageCriteria } from '@luggagehero/shared/interfaces';
import { SharedLocationService } from '@luggagehero/shared/services/locations';
import { SharedLoggingService } from '@luggagehero/shared/services/logging';
import { SharedTranslateService } from '@luggagehero/shared/services/translation';
import { SharedUtilDate } from '@luggagehero/shared/util';
import { BehaviorSubject, Observable } from 'rxjs';

import { SharedStorageCriteria } from './shared-storage-criteria';

@Injectable({
  providedIn: 'root',
})
export class SharedStorageCriteriaService {
  private locationService = inject(SharedLocationService);
  private appSettingsService = inject(SharedAppSettingsService);
  private translateService = inject(SharedTranslateService);
  private log = inject(SharedLoggingService);

  public change: Observable<StorageCriteria>;
  public addressChange: Observable<string>;
  public changeRequested = new EventEmitter<StorageCriteria>();

  private _current: BehaviorSubject<StorageCriteria>;
  private _address: BehaviorSubject<string>;
  private _next: StorageCriteria | null = null;
  private _timeout!: NodeJS.Timeout;

  constructor() {
    this._current = new BehaviorSubject<StorageCriteria>(null as unknown as StorageCriteria);
    this._address = new BehaviorSubject<string>(null as unknown as string);

    this.change = this._current.asObservable();
    this.change.subscribe((criteria) => void this.log.debug(`Criteria changed`, criteria));

    this.addressChange = this._address.asObservable();
  }

  get currentOrDefault(): StorageCriteria {
    return this.current || this.default;
  }

  get current(): StorageCriteria {
    return this._next || this._current.value;
  }
  set current(value: StorageCriteria) {
    this.updateCurrentCriteria(value, true);
  }

  get location(): ILocation | null | undefined {
    return this.current ? this.current.location : null;
  }
  set location(value: ILocation) {
    const current = this.currentOrDefault;
    this.current = new SharedStorageCriteria(value, current.period, current.luggage);
  }

  setLocationWithSource(value: ILocation, coordinateSource: CoordinateSource): void {
    const current = this.currentOrDefault;
    this.current = new SharedStorageCriteria(
      value,
      current.period,
      current.luggage,
      current.activeOnly,
      coordinateSource,
    );
  }

  get period(): ITimePeriod | null | undefined {
    return this.current ? this.current.period : null;
  }
  set period(value: ITimePeriod) {
    const current = this.currentOrDefault;
    this.current = new SharedStorageCriteria(
      current.location,
      this.sanitizePeriod(value),
      current.luggage,
      current.activeOnly,
      current.coordinateSource,
    );
  }

  get from(): Date | null | undefined {
    return this.current ? this.current.period?.from : null;
  }
  set from(value: Date) {
    const current = this.currentOrDefault;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const to = SharedUtilDate.getValidTo(value, (current.period as ITimePeriod).to);
    this.period = { from: value, to: to };
  }

  get to(): Date | null | undefined {
    return this.current ? this.current.period?.to : null;
  }
  set to(value: Date) {
    const current = this.currentOrDefault;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const from = SharedUtilDate.getValidFrom((current.period as ITimePeriod).from, value);
    this.period = { from: from, to: value };
  }

  get luggage(): ILuggage | null | undefined {
    return this.current ? this.current.luggage : null;
  }
  set luggage(value: ILuggage) {
    const current = this.currentOrDefault;
    this.current = new SharedStorageCriteria(
      current.location,
      current.period,
      this.sanitizeLuggage(value, false),
      current.activeOnly,
      current.coordinateSource,
    );
  }

  get default(): StorageCriteria {
    if (!this.locationService.isInitialized) {
      return null as unknown as StorageCriteria;
    }
    const location = this.getDefaultLocation();
    const period = this.getDefaultPeriod();
    const luggage = this.getDefaultLuggage();
    return new SharedStorageCriteria(location ?? undefined, period, luggage);
  }

  private get settings(): SharedAppSettings {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    return this.appSettingsService.current as SharedAppSettings;
  }

  public getMinimumBags(dropOff: boolean): number {
    const enabled = dropOff ? this.settings.IS_DROP_OFF_MIN_BAGS_ENABLED : this.settings.IS_BOOKING_MIN_BAGS_ENABLED;
    const minBags = dropOff ? this.settings.DROP_OFF_MIN_BAGS : this.settings.BOOKING_MIN_BAGS;

    if (enabled && typeof minBags === 'number' && minBags > 0) {
      return minBags;
    }
    return 1;
  }

  public getMinimumBagsWarning(luggage: ILuggage, dropOff: boolean): string | null {
    const numberOfBags =
      (typeof luggage?.normal === 'number' ? luggage.normal : 1) +
      (typeof luggage?.hand === 'number' ? luggage.hand : 0);

    const minBags = this.getMinimumBags(dropOff);

    if (numberOfBags >= minBags) {
      return null;
    }

    const part1 = this.translateService.instant('ADD_UP_TO_X_BAGS');
    const part2 = `${minBags} ${this.translateService.instant('BAGS').toLowerCase()}`;
    const part3 = this.translateService.instant('WITHOUT_PAYING_MORE_AT_LOCATION').toLowerCase();

    return `${part1} ${part2} ${part3}`;
  }

  public updateCurrentCriteria(value: StorageCriteria, debounce: boolean): void {
    const criteria = this.sanitizeCriteria(value);
    if (!criteria) {
      return;
    }
    this._next = criteria;

    // Immediately emit change request event so listeners can know changes are coming
    this.changeRequested.emit(this._next);

    clearTimeout(this._timeout);

    if (debounce) {
      // Avoid broadcasting changes repeatedly when many small adjustments are made quickly
      this._timeout = setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        this._current.next(this._next as StorageCriteria);
        this._next = null;
      }, 2000);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      this._current.next(this._next as StorageCriteria);
      this._next = null;
    }

    if (this._next?.location?.address) {
      this._address.next(this._next.location.address);
    }
  }

  public updateAddress(address: string, region: string) {
    const current = this.currentOrDefault;
    // Create a new location object instead of mutating the existing one
    const newLocation: ILocation = { ...current.location, address, region } as ILocation;
    this.current = new SharedStorageCriteria(
      newLocation,
      current.period,
      current.luggage,
      current.activeOnly,
      current.coordinateSource,
    );
    this._address.next(address);
  }

  public getDefaultLocation(): ILocation | null {
    return this.locationService.nearestOfficialLocation;
  }

  public getDefaultPeriod(): ITimePeriod {
    let from = new Date();

    if (!this.settings.IS_REALTIME_AVAILABILITY_ENABLED) {
      if (from.getHours() < 12) {
        // Before 12pm move forward to 12pm
        from.setHours(12, 0, 0, 0);
      }

      if (from.getHours() >= 18) {
        // After 6pm move to 12pm next day
        from = SharedUtilDate.addDays(from, 1);
        from.setHours(12, 0, 0, 0);
      }

      from = SharedUtilDate.roundTimeHalfHour(from);
    }

    const to = SharedUtilDate.addHours(from, 1);

    return { from, to };
  }

  public getDefaultLuggage(): ILuggage {
    return this.sanitizeLuggage(this.settings.BOOKING_DEFAULT_NUMBER_OF_BAGS, 0);
  }

  public sanitizeCriteria(value: StorageCriteria): StorageCriteria | null {
    if (!value || (this._next && value.equals(this._next))) {
      // Value is empty or didn't change
      return null;
    }

    if (value.period) {
      value.period = this.sanitizePeriod(value.period);
    }

    if (value.luggage) {
      value.luggage = this.sanitizeLuggage(value.luggage);
    }

    return value;
  }

  public sanitizePeriod(value: ITimePeriod): ITimePeriod {
    value.to = SharedUtilDate.getValidTo(value.from, value.to);
    return value;
  }

  public sanitizeLuggage(normal: number, hand: number, dropOff?: boolean): ILuggage;
  public sanitizeLuggage(value: ILuggage, dropOff?: boolean): ILuggage;
  public sanitizeLuggage(
    numberOfNormalBagsOrLuggage: ILuggage | number,
    numberOfHandBagsOrDropoff?: number | boolean,
    dropOff?: boolean,
  ): ILuggage {
    const luggage =
      typeof numberOfNormalBagsOrLuggage === 'number'
        ? {
            normal: numberOfNormalBagsOrLuggage,
            hand: typeof numberOfHandBagsOrDropoff === 'number' ? numberOfHandBagsOrDropoff : 0,
          }
        : {
            normal: numberOfNormalBagsOrLuggage?.normal,
            hand: numberOfNormalBagsOrLuggage?.hand,
          };

    const minBags = typeof dropOff === 'boolean' ? this.getMinimumBags(dropOff) : 1;

    luggage.normal = Math.max(luggage?.normal || 1, minBags);
    luggage.hand = luggage?.hand || 0;

    return luggage;
  }
}
